// The save: everything this browser remembers, read back from storage. It
// is outside input like any other: an old version, a hand-edited value or a
// half-written write. So every part is checked here, and a bad part falls
// back to empty on its own without losing the rest.
//
// Versions (never break an old save; see CHANGELOG.md):
//   ternion.v1  the progress, alone (game versions 0.1 and 0.2)
//   ternion.v2  { version: 2, muted, current, players: [...] } (0.3 to 0.6):
//               several named readers, each with their own progress
//   ternion.v3  { version: 3, muted, progress } (0.7 on): no readers
// The newest one present is read. From a v2 save the reader who was playing
// is kept (or else the first); the others' progress stays in the v2 entry,
// which, like the v1 entry, is left as it was. The Deep (up to 0.16) is
// read past: its records, a rite of it in play and its fragment.

import * as z from "zod/mini"
import { FRAGMENT_IDS, type FragmentId } from "../config/fragments.ts"
import { LAW_IDS, type LawId } from "../config/laws.ts"
import type { SavedPlay } from "../domain/game.ts"
import { EMPTY_PROGRESS, keysOf, type Best, type NoteKey, type Progress, type Witnessed } from "../domain/progress.ts"
import { TERNION_ID, type RiteId, type WonKey } from "../domain/rite.ts"
import type { GlyphKey } from "../domain/rings.ts"
import { moveJsonOf, MoveJson, moveOf } from "./rites.ts"

export { SAVE_KEY, V1_SAVE_KEY, V2_SAVE_KEY } from "./keys.ts"
export const SAVE_VERSION = 3

// Everything this browser remembers.
export type Saved = { readonly progress: Progress; readonly muted: boolean }
export const EMPTY_SAVE: Saved = { progress: EMPTY_PROGRESS, muted: false }

// ---------- names checked once, then trusted ----------

const GLYPH = /^[-0+*]{1,3}$/
const RITE = /^[-0+]{3}$/

const glyphKey = (s: string): GlyphKey | undefined => (GLYPH.test(s) ? (s as GlyphKey) : undefined)
const riteId = (s: string): RiteId | undefined => (RITE.test(s) ? (s as RiteId) : undefined)
const wonKey = (s: string): WonKey | undefined => (s === TERNION_ID ? TERNION_ID : riteId(s))
const lawId = (s: string): LawId | undefined => LAW_IDS.find((l) => l === s)
const fragmentId = (s: string): FragmentId | undefined => FRAGMENT_IDS.find((f) => f === s)
const noteKey = (s: string): NoteKey | undefined => {
  // Notes on laws (0.2 to 0.9) are read past and dropped: only glyphs keep notes.
  const [kind, rest = ""] = s.split(/:(.*)/s)
  return kind === "glyph" && glyphKey(rest) !== undefined ? (s as NoteKey) : undefined
}

// A record whose keys and values are each checked; a bad entry is dropped.
const recordOf = <K extends string, V>(raw: unknown, key: (s: string) => K | undefined, value: (v: unknown) => V | undefined): Readonly<Partial<Record<K, V>>> => {
  const out: Partial<Record<K, V>> = {}
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return out
  for (const [k, v] of Object.entries(raw)) {
    const kk = key(k)
    const vv = value(v)
    if (kk !== undefined && vv !== undefined) out[kk] = vv
  }
  return out
}

const via =
  <S extends z.ZodMiniType>(schema: S) =>
  (v: unknown): z.output<S> | undefined => {
    const r = schema.safeParse(v)
    return r.success ? r.data : undefined
  }

const COUNT = z.int().check(z.minimum(0))
const TIME = z.number() // finite: Zod refuses Infinity and NaN
const COUNT_OR_NONE = z.optional(COUNT)

// ---------- the parts of progress ----------

const SAVED_PLAY_JSON = z.object({
  moves: z.array(MoveJson),
  made: COUNT_OR_NONE,
  aid: COUNT_OR_NONE,
  hints: COUNT_OR_NONE,
  undos: COUNT_OR_NONE,
  rosettaPaid: z.optional(z.boolean()),
})
const savedPlayOf = (p: z.output<typeof SAVED_PLAY_JSON>): SavedPlay => ({
  moves: p.moves.map(moveOf),
  made: p.made ?? p.moves.length,
  aid: p.aid ?? p.hints ?? 0,
  undos: p.undos ?? 0,
  rosettaPaid: p.rosettaPaid ?? false,
})
const SAVED_PLAY = z.pipe(SAVED_PLAY_JSON, z.transform(savedPlayOf))

// Older saves also kept a score here; it is read past and dropped.
const BEST = z.object({ moves: COUNT, aid: COUNT_OR_NONE, hints: COUNT_OR_NONE, pure: z.optional(z.boolean()), at: z.optional(TIME) })

const bestOf = (v: unknown): Best | undefined => {
  const r = BEST.safeParse(v)
  if (!r.success) return undefined
  const aid = r.data.aid ?? r.data.hints ?? 0
  return { moves: r.data.moves, aid, pure: r.data.pure ?? aid === 0, at: r.data.at ?? 0 }
}

const glyphsIn = (k: unknown): readonly GlyphKey[] => (typeof k === "string" && glyphKey(k) !== undefined ? [k as GlyphKey] : [])
const WITNESSED_JSON = z.object({ n: COUNT, example: z.optional(z.array(z.unknown())) })
const witnessedOf = (w: z.output<typeof WITNESSED_JSON>): Witnessed => ({ n: w.n, example: (w.example ?? []).flatMap(glyphsIn) })
const WITNESSED = z.pipe(WITNESSED_JSON, z.transform(witnessedOf))

const field = (raw: unknown, name: string): unknown =>
  typeof raw === "object" && raw !== null && !Array.isArray(raw) ? Object.entries(raw).find(([k]) => k === name)?.[1] : undefined

const trueOnly = (v: unknown): true | undefined => (v === true ? true : undefined)
const text = (v: unknown): string | undefined => (typeof v === "string" ? v.slice(0, 4000) : undefined)
const time = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined)

// The progress, in any version's spelling. A v2 history of wins and the
// Deep are read past and dropped.
export const progressOf = (raw: unknown): Progress => ({
  solved: recordOf(field(raw, "solved"), wonKey, bestOf),
  playing: recordOf(field(raw, "playing"), wonKey, via(SAVED_PLAY)),
  seen: recordOf(field(raw, "seen"), glyphKey, trueOnly),
  laws: recordOf(field(raw, "laws"), lawId, via(WITNESSED)),
  notes: recordOf(field(raw, "notes"), noteKey, text),
  order: recordOf(field(raw, "order"), glyphKey, trueOnly),
  fragments: recordOf(field(raw, "fragments"), fragmentId, time),
  rosetta: field(raw, "rosetta") === true,
})

// ---------- the whole save ----------

// What storage holds under each version's key.
export type Stored = { readonly v3: string | undefined; readonly v2: string | undefined; readonly v1: string | undefined }

// Read what storage holds. Garbage gives an empty save, never an error: the
// game must always open.
export const decodeSave = (stored: Stored): Saved => {
  const v3 = parseJson(stored.v3)
  if (v3 !== undefined) return { progress: progressOf(field(v3, "progress")), muted: field(v3, "muted") === true }
  const v2 = parseJson(stored.v2)
  if (v2 !== undefined) return { progress: progressOf(field(readerOf(v2), "progress")), muted: field(v2, "muted") === true }
  const v1 = parseJson(stored.v1)
  if (v1 !== undefined) return { progress: progressOf(v1), muted: field(v1, "muted") === true }
  return EMPTY_SAVE
}

const parseJson = (s: string | undefined): unknown => {
  if (s === undefined) return undefined
  try {
    const v: unknown = JSON.parse(s)
    return v
  } catch {
    return undefined
  }
}

// The reader of a v2 save who was playing, or else the first one.
const readerOf = (v2: unknown): unknown => {
  const raw = field(v2, "players")
  const players: readonly unknown[] = Array.isArray(raw) ? z.array(z.unknown()).parse(raw) : []
  const current = field(v2, "current")
  return players.find((p) => field(p, "id") === current) ?? players[0]
}

// ---------- writing ----------

const encodePlay = (p: SavedPlay): unknown => ({ moves: p.moves.map(moveJsonOf), made: p.made, aid: p.aid, undos: p.undos, rosettaPaid: p.rosettaPaid })
const mapValues = <K extends string, V>(rec: Readonly<Partial<Record<K, V>>>, f: (v: V) => unknown): unknown => {
  const out: Record<string, unknown> = {}
  for (const k of keysOf(rec)) {
    const v = rec[k]
    if (v !== undefined) out[k] = f(v)
  }
  return out
}

const encodeProgress = (p: Progress): unknown => ({
  ...p,
  playing: mapValues(p.playing, encodePlay),
})

// What storage keeps, as text.
export const encodeSave = (saved: Saved): string => JSON.stringify({ version: SAVE_VERSION, muted: saved.muted, progress: encodeProgress(saved.progress) })
